import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { Modal, Flex, List, Button, Checkbox } from "antd";
import { LockOutlined, TeamOutlined } from "@ant-design/icons";
import { debounce } from "lodash";
import { useNavigate } from "react-router-dom";
import ApiService from "../../../API/apiService";
import { API_ENDPOINTS } from "../../../API/endpoints";
import { handleApiError } from "../../../utils/CommonFunctions";
import { useSocketEvents } from "../../../socket/hooks/useSocketEvents";
import { socketEvents } from "../../../settings/socketEventName";

const BrowseChannelModal = ({
  getChannelListApi,
  userSelfData,
  onModalUpdate,
}) => {
  const { emit } = useSocketEvents();
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const [channelSearchText, setChannelSearchText] = useState("");
  const [hoverState, setHoverState] = useState("");
  const [channelsList, setChannelsList] = useState([]);
  const [filteredChannels, setFilteredChannels] = useState([]);
  const [hideJoined, setHideJoined] = useState(false);

  const searchChannelApi = useCallback(
    async (searchTerm) => {
      const body = {
        searchTerm,
        userId: userSelfData?._id,
      };

      try {
        const response = await ApiService.post(
          API_ENDPOINTS.searchChannelApi,
          body
        );

        if (response?.data && response?.status === 1) {
          const channels = response.data.channels || [];
          setChannelsList(channels);
          applyFilters(channels, hideJoined);
        } else {
          setChannelsList([]);
          setFilteredChannels([]);
        }
      } catch (error) {
        handleApiError(error);
        setChannelsList([]);
        setFilteredChannels([]);
      }
    },
    [userSelfData?._id, hideJoined]
  );

  const debouncedSearch = useMemo(
    () => debounce(searchChannelApi, 600),
    [searchChannelApi]
  );

  const applyFilters = useCallback(
    (channels, shouldHideJoined) => {
      let filtered = channels;

      if (shouldHideJoined) {
        filtered = channels.filter(
          (channel) =>
            !channel.members?.some((member) => member.id === userSelfData._id)
        );
      }

      setFilteredChannels(filtered);
    },
    [userSelfData._id]
  );

  useEffect(() => {
    searchChannelApi("");
    setChannelSearchText("");
    inputRef.current?.focus();
  }, []);

  const handleSearch = useCallback(
    (text) => {
      setChannelSearchText(text);
      debouncedSearch(text);
    },
    [debouncedSearch]
  );

  const handleHideJoinedChange = useCallback(
    (e) => {
      const shouldHide = e.target.checked;
      setHideJoined(shouldHide);
      applyFilters(channelsList, shouldHide);
    },
    [channelsList, applyFilters]
  );

  const addMemberChannel = useCallback(
    async (channelId) => {
      try {
        const body = {
          members: [userSelfData._id],
          auto_join: true,
        };

        const response = await ApiService.put(
          `${API_ENDPOINTS.addMemberChannel}/${channelId}`,
          body
        );

        if(response.status == 1){
          getChannelListApi();
          searchChannelApi("");
          let members = response?.data?.members.map((item) => item?.id);
         emit(socketEvents.ADD_MEMBER, {
          data: {
            senderId: userSelfData?._id,
            receiverId: members,
            channelId: channelId,
          },
        });
        }
      } catch (error) {
        handleApiError(error);
      }
    },
    [userSelfData._id, getChannelListApi, searchChannelApi]
  );

  const handleChannelView = useCallback(
    (channelId) => {
      localStorage.setItem("current_id", channelId);
      navigate(`/elsnertech/channels/${channelId}`);
      onModalUpdate({ activeModal: "" });
    },
    [navigate, onModalUpdate]
  );

  const closeModal = useCallback(() => {
    onModalUpdate({ activeModal: "" });
  }, [onModalUpdate]);

  const isUserJoined = useCallback(
    (channel) => {
      return channel.members?.some((member) => member.id === userSelfData._id);
    },
    [userSelfData._id]
  );

  const renderChannelItem = useCallback(
    (item) => {
      const isJoined = isUserJoined(item);

      return (
        <List.Item
          key={item._id}
          onMouseEnter={() => setHoverState(item._id)}
          className={hoverState === item._id ? "hoverClass" : ""}
        >
          <List.Item.Meta
            avatar={item.isPrivate ? <LockOutlined /> : <TeamOutlined />}
            title={<span className="title">{item.name}</span>}
            description={
              <Flex gap="small">
                {item.joinedStatus && (
                  <span className="join-status">
                    <i className="fa-solid fa-check"></i> Joined
                  </span>
                )}
                <span className="channel-members">
                  <i className="fa-regular fa-user"></i>{" "}
                  {item.members?.length || 0}
                </span>
              </Flex>
            }
          />

          {isJoined ? (
            <Button
              type="text"
              className="view-btn"
              onClick={() => handleChannelView(item._id)}
            >
              View
            </Button>
          ) : (
            <Button
              type="text"
              className="join-btn"
              onClick={() => addMemberChannel(item._id)}
            >
              Join
            </Button>
          )}
        </List.Item>
      );
    },
    [hoverState, isUserJoined, handleChannelView, addMemberChannel]
  );

  return (
    <Modal
      title="Browse Channels"
      open={true}
      onCancel={closeModal}
      footer={null}
      className="ec-modal browse-channels-modal"
      width={600}
      style={{ top: 20 }}
      destroyOnHidden
    >
      <Flex gap="small" className="ant-modal-subheader">
        <div className="search-container">
          <i className="fa-solid fa-magnifying-glass search-icon"></i>
          <input
            autoFocus
            type="text"
            className="custom-search"
            placeholder="Search Channels"
            value={channelSearchText}
            ref={inputRef}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
      </Flex>

      <Flex justify="space-between" className="channel-details">
        <p>{filteredChannels.length} Results</p>
        <Checkbox onChange={handleHideJoinedChange}>Hide Joined</Checkbox>
      </Flex>

      <div className="info__body">
        <Flex vertical className="suggestion-list-container">
          <List
            className="suggestion-list"
            itemLayout="horizontal"
            dataSource={filteredChannels}
            renderItem={renderChannelItem}
          />
        </Flex>
      </div>
    </Modal>
  );
};

export default BrowseChannelModal;
